const emptyLocation = () => ({
    path: null,
    ancestorPaths: [],
    confidence: 'none',
    source: null,
});

const safeDecode = value => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

export const normalizeEpubResource = href => {
    if (!href) return '';

    const resource = String(href).split('#')[0];
    let pathname;

    try {
        pathname = new URL(resource, 'https://epub.invalid/').pathname;
    } catch {
        pathname = resource.split('?')[0];
    }

    return safeDecode(pathname).replace(/^\/+/, '');
};

export const decodeEpubFragment = href => {
    const fragmentIndex = String(href || '').indexOf('#');
    if (fragmentIndex < 0) return '';

    return safeDecode(String(href).slice(fragmentIndex + 1));
};

export const epubResourcesMatch = (left, right) => {
    const normalizedLeft = normalizeEpubResource(left);
    const normalizedRight = normalizeEpubResource(right);
    if (!normalizedLeft || !normalizedRight) return false;
    if (normalizedLeft === normalizedRight) return true;

    return normalizedLeft.endsWith(`/${normalizedRight}`) || normalizedRight.endsWith(`/${normalizedLeft}`);
};

export const readingOrderIndexFor = (readingOrder, href) => {
    const resource = normalizeEpubResource(href);
    if (!resource) return -1;

    const exactIndex = readingOrder.findIndex(item => item === resource);
    if (exactIndex >= 0) return exactIndex;

    const suffixMatches = readingOrder
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => item.endsWith(`/${resource}`) || resource.endsWith(`/${item}`));

    return suffixMatches.length === 1 ? suffixMatches[0].index : -1;
};

const hrefFor = item => item?.chapterHref || item?.href || '';

const normalizeTree = (items, readingOrder) =>
    (Array.isArray(items) ? items : []).flatMap(item => {
        const normalizedChildren = normalizeTree(item.items || item.children, readingOrder);
        const chapterHref = hrefFor(item);
        const hasValidResource = chapterHref && readingOrderIndexFor(readingOrder, chapterHref) >= 0;

        if (!hasValidResource && chapterHref) return normalizedChildren;
        if (!chapterHref && normalizedChildren.length === 0) return [];

        const normalizedItem = {
            ...item,
            label: item.label || item.title || '',
            chapterHref,
            page: item.page ?? null,
            items: normalizedChildren,
        };
        delete normalizedItem.children;

        return [normalizedItem];
    });

const representedReadingOrderIndexes = (items, readingOrder) => {
    const indexes = new Set();

    const visit = nodes => {
        nodes.forEach(item => {
            const index = readingOrderIndexFor(readingOrder, item.chapterHref);
            if (index >= 0) indexes.add(index);
            visit(item.items || []);
        });
    };

    visit(items);
    return indexes;
};

const normalizedLabel = item => String(item.label || item.title || '').trim();

const correspondingItemIndex = (primaryItems, supplement, readingOrder, matchedIndexes) => {
    const supplementLabel = normalizedLabel(supplement);
    const supplementResourceIndex = readingOrderIndexFor(readingOrder, supplement.chapterHref);
    if (!supplementLabel) return -1;

    const candidates = primaryItems
        .map((item, index) => ({
            index,
            label: normalizedLabel(item),
            resourceIndex: readingOrderIndexFor(readingOrder, item.chapterHref),
        }))
        .filter(
            candidate =>
                !matchedIndexes.has(candidate.index) &&
                candidate.label === supplementLabel &&
                (candidate.resourceIndex < 0 ||
                    supplementResourceIndex < 0 ||
                    candidate.resourceIndex === supplementResourceIndex)
        );

    return (
        (candidates.find(candidate => candidate.resourceIndex === supplementResourceIndex) || candidates[0])?.index ??
        -1
    );
};

const firstReadingOrderIndex = (item, readingOrder) => {
    const indexes = [readingOrderIndexFor(readingOrder, item.chapterHref)];
    item.items?.forEach(child => indexes.push(firstReadingOrderIndex(child, readingOrder)));

    const validIndexes = indexes.filter(index => index >= 0);
    return validIndexes.length ? Math.min(...validIndexes) : Number.MAX_SAFE_INTEGER;
};

const sortByReadingOrder = (items, readingOrder) =>
    items
        .map((item, order) => ({ item, order, readingOrderIndex: firstReadingOrderIndex(item, readingOrder) }))
        .sort((left, right) => left.readingOrderIndex - right.readingOrderIndex || left.order - right.order)
        .map(({ item }) => item);

const readingOrderSpan = (item, readingOrder, cache) => {
    const cached = cache.get(item);
    if (cached) return cached;

    const indexes = [readingOrderIndexFor(readingOrder, item.chapterHref)];
    item.items?.forEach(child => {
        const childSpan = readingOrderSpan(child, readingOrder, cache);
        indexes.push(childSpan.first, childSpan.last);
    });

    const validIndexes = indexes.filter(index => index >= 0 && Number.isFinite(index));
    const span = {
        first: validIndexes.length ? Math.min(...validIndexes) : -1,
        last: validIndexes.length ? Math.max(...validIndexes) : -1,
    };
    cache.set(item, span);

    return span;
};

const deepestSpanningBranch = (items, resourceIndex, readingOrder, cache, depth = 0) => {
    let best = null;

    items.forEach(item => {
        const descendant = deepestSpanningBranch(item.items || [], resourceIndex, readingOrder, cache, depth + 1);
        if (descendant && (!best || descendant.depth > best.depth)) best = descendant;

        const span = readingOrderSpan(item, readingOrder, cache);
        const spansResource = item.items?.length && span.first < resourceIndex && resourceIndex < span.last;
        if (spansResource && (!best || depth > best.depth)) best = { item, depth };
    });

    return best;
};

const insertInSpanningBranch = (items, supplement, resourceIndex, readingOrder) => {
    const branch = deepestSpanningBranch(items, resourceIndex, readingOrder, new WeakMap());
    if (!branch) return false;

    branch.item.items = sortByReadingOrder([...(branch.item.items || []), supplement], readingOrder);
    return true;
};

const mergeReadiumSupplements = (primaryItems, supplementItems, representedIndexes, readingOrder) => {
    const merged = [...primaryItems];
    const matchedIndexes = new Set();

    supplementItems.forEach(supplement => {
        const resourceIndex = readingOrderIndexFor(readingOrder, supplement.chapterHref);
        const isRepresented = resourceIndex >= 0 && representedIndexes.has(resourceIndex);
        const matchIndex = correspondingItemIndex(merged, supplement, readingOrder, matchedIndexes);

        if (matchIndex >= 0 && (isRepresented || resourceIndex < 0)) {
            matchedIndexes.add(matchIndex);
            merged[matchIndex] = {
                ...merged[matchIndex],
                items: mergeReadiumSupplements(
                    merged[matchIndex].items || [],
                    supplement.items || [],
                    representedIndexes,
                    readingOrder
                ),
            };
            return;
        }

        if (isRepresented) {
            const promotedChildren = mergeReadiumSupplements(
                merged,
                supplement.items || [],
                representedIndexes,
                readingOrder
            );
            merged.splice(0, merged.length, ...promotedChildren);
            return;
        }

        const items = mergeReadiumSupplements([], supplement.items || [], representedIndexes, readingOrder);
        if (resourceIndex < 0 && items.length === 0) return;

        const normalizedSupplement = { ...supplement, items };
        if (resourceIndex >= 0 && insertInSpanningBranch(merged, normalizedSupplement, resourceIndex, readingOrder)) {
            return;
        }

        merged.push(normalizedSupplement);
    });

    return sortByReadingOrder(merged, readingOrder);
};

export const buildEffectiveEpubToc = (farfallaToc, readiumToc, readingOrderHrefs) => {
    const readingOrder = (Array.isArray(readingOrderHrefs) ? readingOrderHrefs : [])
        .map(normalizeEpubResource)
        .filter(Boolean);
    const normalizedFarfalla = normalizeTree(farfallaToc, readingOrder);
    const normalizedReadium = normalizeTree(readiumToc, readingOrder);

    if (normalizedFarfalla.length === 0) return normalizedReadium;

    return mergeReadiumSupplements(
        normalizedFarfalla,
        normalizedReadium,
        representedReadingOrderIndexes(normalizedFarfalla, readingOrder),
        readingOrder
    );
};

const targetKeyFor = href => `${normalizeEpubResource(href)}#${decodeEpubFragment(href)}`;

export const buildEpubTocIndex = (toc, readingOrderHrefs) => {
    const readingOrder = (Array.isArray(readingOrderHrefs) ? readingOrderHrefs : [])
        .map(normalizeEpubResource)
        .filter(Boolean);
    const entries = [];
    let sourceOrder = 0;

    const visit = (items, parentPath = [], ancestorPaths = []) => {
        (Array.isArray(items) ? items : []).forEach((item, index) => {
            const path = [...parentPath, index];
            const resource = normalizeEpubResource(item.chapterHref);
            const fragment = decodeEpubFragment(item.chapterHref);
            const readingOrderIndex = readingOrderIndexFor(readingOrder, resource);

            if (resource && readingOrderIndex >= 0) {
                entries.push({
                    item,
                    path,
                    ancestorPaths,
                    resource,
                    fragment,
                    targetKey: targetKeyFor(item.chapterHref),
                    readingOrderIndex,
                    sourceOrder: sourceOrder++,
                    depth: path.length,
                });
            }

            visit(item.items, path, [...ancestorPaths, path]);
        });
    };

    visit(toc);

    return { entries, readingOrder };
};

const canonicalEntry = entries =>
    [...entries].sort((left, right) => right.depth - left.depth || left.sourceOrder - right.sourceOrder)[0] ?? null;

const locationFor = (entry, confidence, source) =>
    entry
        ? {
              path: entry.path,
              ancestorPaths: entry.ancestorPaths,
              confidence,
              source,
          }
        : emptyLocation();

const previousStart = (entries, currentReadingOrderIndex) => {
    const previousEntries = entries.filter(entry => entry.readingOrderIndex < currentReadingOrderIndex);
    if (previousEntries.length === 0) return null;

    const latestResourceIndex = Math.max(...previousEntries.map(entry => entry.readingOrderIndex));
    const latestEntries = previousEntries.filter(entry => entry.readingOrderIndex === latestResourceIndex);
    const lastTargetOrder = Math.max(...latestEntries.map(entry => entry.sourceOrder));
    const lastTarget = latestEntries.find(entry => entry.sourceOrder === lastTargetOrder);

    return canonicalEntry(latestEntries.filter(entry => entry.targetKey === lastTarget.targetKey));
};

export const resolveEpubTocLocation = ({ index, locatorHref, validAnchorKeys = [], startedAnchorKeys = [] }) => {
    const currentReadingOrderIndex = readingOrderIndexFor(index?.readingOrder || [], locatorHref);
    if (currentReadingOrderIndex < 0) return emptyLocation();

    const entries = index?.entries || [];
    const firstStartIndex = Math.min(...entries.map(entry => entry.readingOrderIndex));
    if (!Number.isFinite(firstStartIndex) || currentReadingOrderIndex < firstStartIndex) return emptyLocation();

    const currentEntries = entries.filter(entry => entry.readingOrderIndex === currentReadingOrderIndex);
    const currentAnchors = currentEntries.filter(entry => entry.fragment);
    const validAnchors = new Set(validAnchorKeys);
    const startedAnchors = startedAnchorKeys.filter(key => validAnchors.has(key));

    if (startedAnchors.length > 0) {
        const currentTargetKey = startedAnchors[startedAnchors.length - 1];
        const entry = canonicalEntry(currentAnchors.filter(candidate => candidate.targetKey === currentTargetKey));
        if (entry) return locationFor(entry, 'exact', 'fragment');
    }

    const resourceEntry = canonicalEntry(currentEntries.filter(entry => !entry.fragment));
    if (resourceEntry) return locationFor(resourceEntry, 'parent', 'resource');

    return locationFor(previousStart(entries, currentReadingOrderIndex), 'parent', 'spine-interval');
};

export const getEpubTocItemAtPath = (toc, path) => {
    if (!Array.isArray(path)) return null;

    let items = toc;
    let item = null;
    for (const index of path) {
        item = items?.[index] ?? null;
        if (!item) return null;
        items = item.items;
    }

    return item;
};

export const getEpubTocItemsAtPaths = (toc, paths) =>
    (Array.isArray(paths) ? paths : []).map(path => getEpubTocItemAtPath(toc, path)).filter(Boolean);

export const selectEpubDocumentTitleItem = (currentItem, ancestors = []) => {
    if (!currentItem) return null;

    const unanchoredAncestor = [...ancestors]
        .reverse()
        .find(
            item =>
                !decodeEpubFragment(item.chapterHref) && epubResourcesMatch(item.chapterHref, currentItem.chapterHref)
        );

    return unanchoredAncestor || currentItem;
};

export const epubTocLocationsEqual = (left, right) =>
    left?.confidence === right?.confidence &&
    left?.source === right?.source &&
    JSON.stringify(left?.path) === JSON.stringify(right?.path) &&
    JSON.stringify(left?.ancestorPaths || []) === JSON.stringify(right?.ancestorPaths || []);

export { emptyLocation as createEmptyEpubTocLocation };
